import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, QrCode, Smartphone, FileText, Building2, Lock,
  ArrowRight, CheckCircle2, Server, ScrollText, Clock,
} from "lucide-react";
import { blogPosts } from "@/data/blogPosts";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { useSeo } from "@/hooks/useSeo";

const FEATURES = [
  { icon: ShieldCheck, title: "DSGVO-konform by Design", text: "Versionierte Datenschutzerklärung, dokumentierte Einwilligung mit Zeitstempel und revisionssichere Audit-Logs." },
  { icon: QrCode, title: "QR-Code-Patientenflow", text: "Patient:innen scannen am Empfang und füllen alles selbst aus – ganz ohne Wartezeit am Tresen." },
  { icon: Smartphone, title: "Unterschrift am Tablet & Handy", text: "Handschriftliche Signatur direkt am Praxis-iPad oder bequem auf dem eigenen Smartphone – schnell und rechtssicher." },
  { icon: FileText, title: "Automatische PDF-Generierung", text: "Jede Einwilligung wird als revisionssichere PDF mit Volltext, Unterschrift und Metadaten erzeugt." },
  { icon: Building2, title: "Multi-Mandantenfähig", text: "Mehrere Praxen, strikt getrennte Daten, eigene Logos und Datenschutztexte – zentral verwaltet." },
  { icon: Lock, title: "Verschlüsselte Signaturen", text: "Biometrische Unterschriftsdaten werden per AES-GCM mit praxisindividuellem Schlüssel verschlüsselt." },
];

const TRUST = [
  { icon: Server, title: "Hosting in Deutschland", text: "Datenhaltung in deutschen EU-Rechenzentren (MongoDB Atlas Frankfurt)." },
  { icon: ScrollText, title: "Hash-verkettete Audit-Logs", text: "Jede Aktion unveränderlich protokolliert und als CSV exportierbar." },
  { icon: Clock, title: "10-Jahres-Aufbewahrung", text: "Automatische Fristenkontrolle gemäß § 630f BGB inklusive Soft-Delete & Wiederherstellung." },
];

export default function LandingPage() {
  useSeo(
    "LAIzyDocu – DSGVO-konforme Patienteneinwilligung digital am Tablet & Handy",
    "LAIzyDocu digitalisiert die Patientenaufnahme für Praxen: QR-Code-Patientenflow, Unterschrift am iPad oder Smartphone, automatische PDF und höchste DSGVO-Standards. Jetzt Testzugang anfragen.",
  );

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <SiteNav />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-sm font-medium">
              <CheckCircle2 className="w-4 h-4" /> DSGVO-konform · Hosting in Deutschland
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-emerald-900 leading-[1.05] mt-6">
              Die Digitalisierung der Patientenaufnahme.
            </h1>
            <p className="text-lg text-stone-600 mt-6 max-w-xl leading-relaxed">
              LAIzyDocu erfasst die Einwilligung zur Datenschutzerklärung und zum Behandlungsvertrag
              vollständig digital – per QR-Code, Unterschrift am iPad <strong>oder Handy</strong> und
              automatischer PDF. Rechtssicher, papierlos und in unter zwei Minuten.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to="/testzugang" data-testid="landing-hero-cta">
                <Button className="h-14 px-7 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white text-base w-full sm:w-auto">
                  Kostenlosen Testzugang anfragen <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/preise">
                <Button variant="secondary" className="h-14 px-7 rounded-xl bg-white border border-stone-200 hover:bg-stone-100 text-stone-900 text-base w-full sm:w-auto">
                  Preise ansehen
                </Button>
              </Link>
            </div>
            <p className="text-sm text-stone-400 mt-4">Freischaltung nach kurzer Prüfung · Kostenloser Testzeitraum auf Anfrage</p>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-emerald-900/5 rounded-[2.5rem] -rotate-2" aria-hidden />
            <div className="relative bg-white rounded-[2rem] border border-stone-100 shadow-[0_20px_60px_rgb(0,0,0,0.06)] p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-900 text-white grid place-items-center">
                  <QrCode className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-400">Patientenflow</div>
                  <div className="font-display text-lg font-semibold text-emerald-900">Scannen · Lesen · Unterschreiben</div>
                </div>
              </div>
              <ol className="space-y-4">
                {["QR-Code am Empfang scannen (Handy oder iPad)", "Daten eingeben & Datenschutz lesen", "Behandlungsvertrag bestätigen", "Am Display unterschreiben", "PDF wird automatisch erstellt"].map((s, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-emerald-50 text-emerald-800 grid place-items-center text-sm font-semibold">{i + 1}</span>
                    <span className="text-stone-700">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-emerald-900">Alles für die rechtssichere Einwilligung</h2>
          <p className="text-stone-600 mt-3 text-lg">Von der Aufnahme bis zur Archivierung – ein durchdachter Prozess, der den Praxisalltag entlastet.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {FEATURES.map((f) => (
            <div key={f.title} data-testid={`feature-${f.title}`} className="bg-white rounded-3xl border border-stone-100 p-7 hover:shadow-[0_12px_40px_rgb(0,0,0,0.05)] transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-800 grid place-items-center mb-5">
                <f.icon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-semibold text-emerald-900">{f.title}</h3>
              <p className="text-stone-600 mt-2 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust */}
      <section className="bg-emerald-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold">Datenschutz auf höchstem Niveau</h2>
          <p className="text-emerald-100/80 mt-3 text-lg max-w-2xl">Wir behandeln Gesundheitsdaten mit der Sorgfalt, die sie verdienen.</p>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {TRUST.map((t) => (
              <div key={t.title} className="bg-emerald-800/40 rounded-3xl border border-emerald-700/40 p-7">
                <div className="w-12 h-12 rounded-xl bg-white/10 grid place-items-center mb-5">
                  <t.icon className="w-6 h-6 text-emerald-100" />
                </div>
                <h3 className="font-display text-xl font-semibold">{t.title}</h3>
                <p className="text-emerald-100/80 mt-2 leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog preview */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-emerald-900">Aus dem Magazin</h2>
            <p className="text-stone-600 mt-3 text-lg">Wissen rund um Datenschutz und Digitalisierung in der Praxis.</p>
          </div>
          <Link to="/blog" data-testid="landing-blog-all" className="inline-flex items-center gap-1 text-emerald-900 font-medium hover:underline">
            Alle Artikel <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {blogPosts.slice(0, 3).map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`landing-blog-card-${p.slug}`}
              className="group bg-white rounded-3xl border border-stone-100 p-7 hover:shadow-[0_12px_40px_rgb(0,0,0,0.05)] transition-shadow block">
              <div className="flex items-center gap-2 text-xs text-stone-400 mb-3">
                <span>{new Date(p.date).toLocaleDateString("de-DE")}</span>
                <span className="w-1 h-1 rounded-full bg-stone-300" />
                <span>{p.readingMinutes} Min. Lesezeit</span>
              </div>
              <h3 className="font-display text-lg font-semibold text-emerald-900 group-hover:text-emerald-700 leading-snug">{p.title}</h3>
              <p className="text-stone-600 mt-2 text-sm leading-relaxed line-clamp-3">{p.excerpt}</p>
              <span className="inline-flex items-center gap-1 text-emerald-900 font-medium text-sm mt-4">Weiterlesen <ArrowRight className="w-4 h-4" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-20">
        <div className="bg-white rounded-[2rem] border border-stone-100 shadow-[0_20px_60px_rgb(0,0,0,0.05)] p-10 md:p-14 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-emerald-900 max-w-2xl mx-auto">Bereit, Ihre Patientenaufnahme zu digitalisieren?</h2>
          <p className="text-stone-600 mt-4 text-lg max-w-xl mx-auto">Fordern Sie jetzt Ihren Testzugang an. Nach kurzer Prüfung schalten wir Ihre Praxis frei.</p>
          <Link to="/testzugang" data-testid="landing-cta-bottom" className="inline-block mt-8">
            <Button className="h-14 px-8 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white text-base">
              Testzugang anfragen <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
